import { toast } from 'react-toastify';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';




/**
 * Delete a user-defined template using native fetch
 * @param templateId   ID of the template
 * @param templateName Optional name for nicer toast message
 */
export const deleteUserTemplate = async (
  templateId: string,
  templateName?: string
): Promise<void> => {
  try {
    // Get auth context
    const { token, tenantId, userId } = getAuthContext();

    const response = await fetch(
      `${BASE_URL}/v1/templates/user-define/${templateId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId || '',
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      let errorMessage = 'Failed to delete template';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If response is not JSON
      }
      throw new Error(errorMessage);
    }

    // Success
    toast.success(
      'Template deleted successfully!',
      { autoClose: 2000 }
    );
  } catch (err: any) {
    toast.error(err.message || 'Failed to delete template', {
      autoClose: 5000,
    });

    throw err;
  }
};

export interface TemplatePayload {
  orgId: string;
  description: string;
  status: string;
  version: number;
  folderId: string;
  assetId: string | null;
  name: string;
  styles: string;
  blocks: Array<{
    type: string;
    text: string;
  }>;
  metadata: any;
  capability: any;
  createdBy: string;
  createdAt: string | number;
  updatedAt: string | number;
  expectedVersion?: number;
}

export interface CreateTemplateOptions {
  payload: TemplatePayload;
  files?: File[];
}

export const createUserDefinedTemplate = async ({
  payload,
  files = [],
}: CreateTemplateOptions) => {
  const formData = new FormData();

  formData.append(
    'CreateRequest',
    new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    })
  );

  files.forEach((file) => {
    formData.append('files', file);
  });

  // Get auth context from jwt utility
  const { token, tenantId, userId } = getAuthContext();

  const response = await fetch(
    `${BASE_URL}/v1/templates/user-define`,
    {
      method: 'POST',
      headers: {
        'X-Tenant-Id': tenantId || '',
        'X-User-Id': userId || '',
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    let errorMessage = 'Failed to create template';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (e) {
      errorMessage = await response.text();
    }
    const err = new Error(errorMessage);
    (err as any).status = response.status;
    throw err;
  }

  return await response.json();
};



export interface UpdateTemplateOptions {
  templateId: string;
  payload: TemplatePayload;
  files?: File[];
}

export const updateUserDefinedTemplate = async ({
  templateId,
  payload,
  files = [],
}: UpdateTemplateOptions): Promise<any> => {
  const formData = new FormData();

  formData.append(
    'ReplaceRequest',
    new Blob([JSON.stringify(payload)], {
      type: 'application/json',
    })
  );

  files.forEach((file) => {
    formData.append('files', file);
  });

  // Get auth context
  const { token, tenantId, userId } = getAuthContext();

  const response = await fetch(
    `${BASE_URL}/v1/templates/user-define/${templateId}`,
    {
      method: 'PUT',
      headers: {
        'X-Tenant-Id': tenantId || '',
        'X-User-Id': userId || '',
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    let errorMessage = 'Failed to update template';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // If response is not JSON
    }
    throw new Error(errorMessage);
  }

  return await response.json();
};



export interface UpdateTemplateMetadataPayload {
  name?: string;
  description?: string;
  status?: string;
  metadata?: any;
}


/**
 * Update template metadata (name, description, status, or full metadata)
 * @param templateId ID of the template to update
 * @param payload Object containing fields to update
 */
export const updateTemplateMetadata = async (
  templateId: string,
  payload: UpdateTemplateMetadataPayload | { metadata: any }
): Promise<any> => {
  try {
    const { token, tenantId, userId } = getAuthContext();

    const response = await fetch(
      `${BASE_URL}/v1/templates/user-define/${templateId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId || '',
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      let errorMessage = 'Failed to update template metadata';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = await response.text();
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (err: any) {
    toast.error(err.message || 'Failed to update template metadata', {
      position: 'top-right',
      autoClose: 3000,
    });
    throw err;
  }
};

export const updatePrebuiltTemplateMetadata = async (
  templateId: string,
  payload: UpdateTemplateMetadataPayload
): Promise<any> => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/internal/content/templates/pre-build/${templateId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          // Add auth header if your backend requires it
          // 'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      let errorMessage = 'Failed to update template metadata';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If response is not JSON
        errorMessage = await response.text();
      }
      throw new Error(errorMessage);
    }

    // Parse and return the response
    const data = await response.json();

    // Show success toast
    // const updatedFields = Object.keys(payload).filter(
    //   (key) => payload[key as keyof UpdateTemplateMetadataPayload]
    // );
    // if (updatedFields.length > 0) {
    //   const fieldNames = updatedFields.join(', ');
    //   // toast.success(`${fieldNames} updated successfully`, {
    //   //   position: 'top-right',
    //   //   autoClose: 2000,
    //   // });
    // }

    return data;
  } catch (err: any) {
    // Show error toast
    toast.error(err.message || 'Failed to update template metadata', {
      position: 'top-right',
      autoClose: 3000,
    });

    // Re-throw so the component can handle it
    throw err;
  }
};

/**
 * Fetch a single user-defined template by ID
 * @param templateId ID of the template
 */
export const fetchUserDefinedTemplateById = async (
  templateId: string
): Promise<any> => {
  // Get auth context
  const { token, tenantId, userId } = getAuthContext();

  const response = await fetch(
    `${BASE_URL}/v1/templates/user-define/${templateId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenantId || '',
        'X-User-Id': userId || '',
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch template');
  }

  return await response.json();
};

/**
 * Fetch a prebuilt template by ID
 */
export const fetchPrebuiltTemplateById = async (
  templateId: string
): Promise<any> => {
  // Get auth context for headers - same as other APIs
  const { token, tenantId, userId } = getAuthContext();

  if (!token) {
    throw new Error('Authentication token not available');
  }

  const response = await fetch(
    `${BASE_URL}/v1/templates/pre-build/${templateId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenantId || '',
        'X-User-Id': userId || '',
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch prebuilt template');
  }

  return await response.json();
};

/**
 * Smart template fetcher - determines type by ID prefix or tries both
 */
export const fetchTemplateById = async (
  fromTab: string | undefined,
  templateId: string
): Promise<any> => {
  try {
    if (fromTab === 'yourTemplates') {
      return await fetchUserDefinedTemplateById(templateId);
    } else {
      return await fetchPrebuiltTemplateById(templateId);
    }
    // Try user-defined first (common case)
  } catch (error) {
    // If user-defined fails, try prebuilt
    // return await fetchPrebuiltTemplateById(templateId);
    console.error(error);
  }
};



/**
 * Update template status only
 * @param templateId ID of the template
 * @param status New status (APPROVED, DRAFT, UNDER_REVIEW, etc.)
 */
export const updateTemplateStatus = async (
  templateId: string,
  status: string
): Promise<any> => {
  return updateTemplateMetadata(templateId, { status });
};

/**
 * Update template name only
 * @param templateId ID of the template
 * @param name New template name
 */
export const updateTemplateName = async (
  templateId: string,
  name: string
): Promise<any> => {
  return updateTemplateMetadata(templateId, { name });
};

/**
 * Update template description only
 * @param templateId ID of the template
 * @param description New template description
 */
export const updateTemplateDescription = async (
  templateId: string,
  description: string
): Promise<any> => {
  return updateTemplateMetadata(templateId, { description });
};

/**
 * Duplicate/Copy a template
 * @param templateId ID of the template to duplicate
 * @param newName Optional new name for the duplicate
 */

// Export all functions for easy imports
export default {
  deleteUserTemplate,
  createUserDefinedTemplate,
  updateUserDefinedTemplate,
  updateTemplateMetadata,
  updateTemplateStatus,
  updateTemplateName,
  updateTemplateDescription,
  fetchUserDefinedTemplateById,
  updatePrebuiltTemplateMetadata
};